/**
 * Iter67b — BO leagă explicit un rând de import (coada „Necesită verificare”) la un contract candidat de pe același POD.
 * Regula: POD + client + semnare apropiată NU demonstrează aceeași vânzare → legarea este o decizie umană, auditată.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { formatRoDate } from "../lib/dates";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin/data-reconciliation`;
const fmt = (v) => (v && /^\d{4}-\d{2}-\d{2}/.test(String(v)) ? formatRoDate(String(v)) : v ?? "—");
const Flag = ({ v, label }) => <span className={`rounded px-1 text-[10px] ${v === true ? "bg-emerald-50 text-emerald-700" : v === false ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-500"}`}>{v === true ? "✓" : v === false ? "✗" : "?"} {label}</span>;

export const ImportRowLinkDialog = ({ item, open, onClose, onLinked }) => {
  const [contractId, setContractId] = useState("");
  const [just, setJust] = useState("");
  const [busy, setBusy] = useState(false);
  const [pv, setPv] = useState(null);
  const ev = item?.evidence || {};
  const cands = pv?.candidates?.length ? pv.candidates : (ev.candidates || []);

  useEffect(() => {
    if (!open || !item) return;
    setContractId(""); setJust(""); setPv(null);
    axios.get(`${BASE}/identity/import-row/preview`, { params: { batch_id: ev.batch_id, row: ev.row } }).then((r) => setPv(r.data)).catch(() => setPv(null));
  }, [open, item]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/identity/link-import-row`, { batch_id: ev.batch_id, row: ev.row, contract_id: contractId, justification: just, fingerprint: pv?.fingerprint || null });
      toast.success(r.data.idempotent ? "Decizie deja aplicată (idempotent)" : `Rând legat: ${r.data.field} = ${r.data.external_id} (decizie + audit)`);
      onLinked?.(r.data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Legarea nu a fost aplicată"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-row-link-dialog">
        <DialogHeader>
          <DialogTitle>Leagă rândul de import la un contract</DialogTitle>
          <DialogDescription>Sursa {ev.source} · rând {ev.row} · identificator <span className="font-mono">{ev.new_external_id}</span>. Se scrie identificatorul pe contractul ales și decizia în istoric; rândul din lot rămâne, marcat rezolvat. La reimport, rândul se leagă prin identificator.</DialogDescription>
        </DialogHeader>
        {item && (
          <div className="space-y-3 text-sm">
            <div className="rounded bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900" data-testid="import-row-link-reason">{item.justification}</div>
            {ev.source_row && <div className="rounded border border-slate-200 p-2 text-xs grid grid-cols-2 gap-x-3" data-testid="import-row-link-source-row">
              {Object.entries(ev.source_row).map(([k, v]) => <div key={k} className="contents"><span className="text-slate-500 font-mono">{k}</span><span>{fmt(String(v ?? "—"))}</span></div>)}
            </div>}
            <div className="space-y-1" data-testid="import-row-link-candidates">
              <p className="text-xs font-semibold text-slate-700">Contracte candidate pe POD {item.pod}</p>
              {cands.map((c) => (
                <label key={c.contract_id} className="flex items-start gap-2 text-xs rounded border border-slate-200 p-2">
                  <input type="radio" name="link-candidate" value={c.contract_id} checked={contractId === c.contract_id} onChange={() => setContractId(c.contract_id)} disabled={c.unlinked_for_source === false || (ev.source === "easy" ? !!(c.easy_contract_id || c.contract_number_old) : ev.source === "salesforce" ? !!c.sf_contract_id : false)} data-testid={`import-row-link-cand-${c.contract_id}`} />
                  <span className="space-y-0.5">
                    <span className="font-mono">{c.contract_id.slice(0, 8)}</span> · Easy {c.easy_contract_id || "—"} · SF {c.sf_contract_id || "—"} · semnat {fmt(c.signed_date)} · {c.commodity || "—"} · {c.supplier_status || c.business_status || "—"}
                    <span className="flex flex-wrap gap-1"><Flag v={c.client_match} label="client" /><Flag v={c.commodity_match} label="energie" /><Flag v={c.signed_within_tolerance} label="semnare ≤45 zile" />
                      {c.unlinked_for_source === false && <Badge className="bg-rose-100 text-rose-700 border-0">are deja alt identificator</Badge>}</span>
                  </span>
                </label>
              ))}
            </div>
            <Textarea value={just} onChange={(e) => setJust(e.target.value)} placeholder="Justificare obligatorie: dovada verificată (document, confirmare client/agent, sursă) — min. 10 caractere" data-testid="import-row-link-justification" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="import-row-link-cancel">Renunță</Button>
          <Button onClick={submit} disabled={busy || !contractId || just.trim().length < 10} data-testid="import-row-link-submit">Leagă și înregistrează decizia</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
